import { toPlainText } from './richText';
import type { FinderGroup } from './types';

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export interface FilterState {
  campus: number | null;
  lifeStage: number | null;
  /** 1 = kids welcome, 0 = adults only */
  kids: 0 | 1 | null;
  household: number | null;
  gender: number | null;
  days: string[];
  cities: string[];
  search: string;
  /** Show groups that are full or on a break, after the open ones (default on) */
  showFull: boolean;
  /** Pin the list to specific groups (deep links from emails etc.) */
  groupIds: number[];
  /** Only See Jesus Groups (the campaign's short-term groups) */
  seeJesus: boolean;
}

export const EMPTY_FILTERS: FilterState = {
  campus: null,
  lifeStage: null,
  kids: null,
  household: null,
  gender: null,
  days: [],
  cities: [],
  search: '',
  showFull: true,
  groupIds: [],
  seeJesus: false,
};

/** Number of filters the user has set, for the badge on the Filters button. */
export function countActive(f: FilterState): number {
  let n = 0;
  if (f.campus !== null) n++;
  if (f.lifeStage !== null) n++;
  if (f.kids !== null) n++;
  if (f.household !== null) n++;
  if (f.gender !== null) n++;
  if (f.days.length > 0 && f.days.length < DAYS.length) n++;
  if (f.cities.length > 0) n++;
  if (f.search.trim()) n++;
  if (f.seeJesus) n++;
  return n;
}

// ---------------------------------------------------------------------------
// URL <-> state
//
// Short keys are what the widget writes. The `@CongregationID` family is what
// the old MP CustomWidget embed accepted, and links using it are still out
// there in emails and campus pages, so both are read.
// ---------------------------------------------------------------------------

const KEY_ALIASES: Record<string, keyof FilterState> = {
  campus: 'campus',
  congregationid: 'campus',
  stage: 'lifeStage',
  lifestageid: 'lifeStage',
  kids: 'kids',
  kidswelcome: 'kids',
  household: 'household',
  intendedaudienceid: 'household',
  gender: 'gender',
  genderid: 'gender',
  days: 'days',
  daysofweek: 'days',
  cities: 'cities',
  city: 'cities',
  q: 'search',
  search: 'search',
  keyword: 'search',
  full: 'showFull',
  showfull: 'showFull',
  groups: 'groupIds',
  groupids: 'groupIds',
  sj: 'seeJesus',
  seejesus: 'seeJesus',
};

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function toInt(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Apply a set of raw key/value pairs (from the page URL, or from the embed's
 * data-params attribute) on top of a filter state. Unknown keys are ignored.
 * Campus accepts a numeric Congregation_ID, MP's Campus_Slug ("troy") or a
 * campus name; the caller's `resolveCampus` (see campusIndex.ts) decides.
 */
export function applyRawParams(
  base: FilterState,
  entries: Iterable<[string, string]>,
  resolveCampus: (value: string) => number | null
): FilterState {
  const next: FilterState = { ...base, days: [...base.days], cities: [...base.cities] };
  for (const [rawKey, rawValue] of entries) {
    const key = KEY_ALIASES[rawKey.trim().replace(/^@/, '').toLowerCase()];
    const value = rawValue.trim();
    if (!key || !value) continue;
    switch (key) {
      case 'campus':
        next.campus = resolveCampus(value);
        break;
      case 'lifeStage':
        next.lifeStage = toInt(value);
        break;
      case 'kids': {
        const n = toInt(value);
        next.kids = n === 1 ? 1 : n === 0 ? 0 : null;
        break;
      }
      case 'household':
        next.household = toInt(value);
        break;
      case 'gender':
        next.gender = toInt(value);
        break;
      case 'days': {
        const days = splitList(value)
          .map((d) => DAYS.find((x) => x.toLowerCase() === d.toLowerCase()))
          .filter((d): d is string => Boolean(d));
        // The old embed sent all seven days to mean "any day".
        next.days = days.length === DAYS.length ? [] : days;
        break;
      }
      case 'cities':
        next.cities = splitList(value);
        break;
      case 'search':
        next.search = value;
        break;
      case 'showFull':
        next.showFull = !(value === '0' || value.toLowerCase() === 'false');
        break;
      case 'groupIds':
        next.groupIds = splitList(value)
          .map((s) => toInt(s))
          .filter((n): n is number => n !== null);
        break;
      case 'seeJesus':
        next.seeJesus = !(value === '0' || value.toLowerCase() === 'false');
        break;
    }
  }
  return next;
}

export type SlugFor = (id: number) => string | null;

/**
 * Serialize the user-set filters into short query params. The campus is
 * written as its slug ("troy") when one is known so links read well and
 * match the rest of the site; a bare Congregation_ID is the fallback.
 */
export function filtersToParams(f: FilterState, slugFor?: SlugFor): URLSearchParams {
  const p = new URLSearchParams();
  if (f.campus !== null) p.set('campus', slugFor?.(f.campus) ?? String(f.campus));
  if (f.lifeStage !== null) p.set('stage', String(f.lifeStage));
  if (f.kids !== null) p.set('kids', String(f.kids));
  if (f.household !== null) p.set('household', String(f.household));
  if (f.gender !== null) p.set('gender', String(f.gender));
  if (f.days.length > 0 && f.days.length < DAYS.length) p.set('days', f.days.join(','));
  if (f.cities.length > 0) p.set('cities', f.cities.join(','));
  if (f.search.trim()) p.set('q', f.search.trim());
  if (!f.showFull) p.set('full', '0');
  if (f.groupIds.length > 0) p.set('groups', f.groupIds.join(','));
  if (f.seeJesus) p.set('sj', '1');
  return p;
}

const MANAGED_KEYS = new Set(Object.keys(KEY_ALIASES));

/**
 * Rewrite the page URL so the current filters are shareable, leaving every
 * query param the widget doesn't own (WordPress previews, UTM tags) intact.
 */
export function syncFiltersToUrl(f: FilterState, slugFor?: SlugFor): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    for (const key of Array.from(url.searchParams.keys())) {
      if (MANAGED_KEYS.has(key.replace(/^@/, '').toLowerCase())) url.searchParams.delete(key);
    }
    for (const [k, v] of filtersToParams(f, slugFor)) url.searchParams.set(k, v);
    const next = url.toString();
    if (next !== window.location.href) window.history.replaceState(window.history.state, '', next);
  } catch {
    /* URL APIs unavailable, nothing to sync */
  }
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function haystack(g: FinderGroup): string {
  return normalize(
    [
      g.title,
      toPlainText(g.description),
      g.city,
      g.day,
      g.occurrence,
      g.campusName ?? '',
      g.lifeStage ?? '',
      g.householdType ?? '',
      g.gender ?? '',
      ...g.leaders.map((l) => l.name),
      ...g.keywords,
      g.seeJesus ? 'see jesus group' : '',
    ].join(' ')
  );
}

export function matches(g: FinderGroup, f: FilterState, hay: string = haystack(g)): boolean {
  if (f.groupIds.length > 0 && !f.groupIds.includes(g.id)) return false;
  if (f.seeJesus && !g.seeJesus) return false;
  if (f.campus !== null && g.campusId !== f.campus) return false;
  if (f.lifeStage !== null && g.lifeStageId !== f.lifeStage) return false;
  if (f.kids !== null && g.kidsWelcome !== (f.kids === 1)) return false;
  if (f.household !== null && g.householdTypeId !== f.household) return false;
  if (f.gender !== null && g.genderId !== f.gender) return false;
  if (f.days.length > 0 && f.days.length < DAYS.length && !f.days.includes(g.day)) return false;
  if (f.cities.length > 0) {
    const city = normalize(g.city);
    if (!f.cities.some((c) => normalize(c) === city)) return false;
  }
  const terms = normalize(f.search).split(/\s+/).filter(Boolean);
  if (terms.length > 0 && !terms.every((t) => hay.includes(t))) return false;
  return true;
}

export interface FilterResult {
  /** Groups with room, matching every filter */
  open: FinderGroup[];
  /** Groups matching every filter that are full or on a break */
  unavailable: FinderGroup[];
}

/**
 * Split the catalogue into the groups someone can actually join today and
 * the ones that match but are full or paused. The order inside each half is
 * the proc's (api_custom_GroupFinderWidget_JSON): open groups shuffled on
 * every run so no group camps at the top, then full, then paused. Nothing
 * here re-sorts, so the finder and site search agree.
 */
export function filterGroups(groups: FinderGroup[], f: FilterState): FilterResult {
  const open: FinderGroup[] = [];
  const unavailable: FinderGroup[] = [];
  for (const g of groups) {
    if (!matches(g, f)) continue;
    if (g.full || g.paused) unavailable.push(g);
    else open.push(g);
  }
  return { open, unavailable };
}
